import React, { useState, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../utils/toast";
import { useSearchParams } from "react-router-dom";

// Load PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Insurance companies list
const insuranceCompanies = [
  "Tata AIG General Insurance Company",
  "Tata AIA Life Insurance",
  "ACKO HEALTH",
  "HDFC ERGO General Insurance",
  "ICICI Lombard General Insurance",
  "Bajaj Allianz General Insurance",
  "Reliance General Insurance",
  "SBI General Insurance",
  "New India Assurance",
  "United India Insurance",
  "National Insurance Company",
  "Oriental Insurance",
  "IFFCO Tokio General Insurance",
  "Cholamandalam MS General Insurance",
  "Future Generali India Insurance",
  "Kotak Mahindra General Insurance",
  "Niva Bupa Health Insurance",
];

const AddInsurancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [showCustomCompany, setShowCustomCompany] = useState(false);
  const [customCompany, setCustomCompany] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [editHolder, setEditHolder] = useState(false);
  const [editPolicy, setEditPolicy] = useState(false);
  const [editFamily, setEditFamily] = useState(false);

  const [searchParams] = useSearchParams();
  const [manualForm, setManualForm] = useState(false);

  useEffect(() => {
    const mode = searchParams.get("mode");
    setManualForm(mode === "manual");
  }, [searchParams]);

  // Manual form state
  const [formData, setFormData] = useState({
    policyHolderName: "",
    policyHolderPhone: "",
    policyHolderAddress: "",
    policyNumber: "",
    productName: "",
    policyStartDate: "",
    policyEndDate: "",
    tenure: "",
    endDate: "",
    policyPeriod: "",
    policyType: ""
  });

  // PDF states
  const [pdfData, setPdfData] = useState({});
  const [insuredMembers, setInsuredMembers] = useState([]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePDFChange = (e) => {
    const { name, value } = e.target;
    setPdfData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInsuredMemberChange = (index, field, value) => {
    setInsuredMembers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Handle company selection for Step 1
  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    
    if (value === 'other') {
      setShowCustomCompany(true);
    } else {
      setShowCustomCompany(false);
      setCustomCompany('');
    }
  };

  // Handle company selection for Manual Form
  const handleManualCompanyChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, policyType: value }));
  };

  const handleManualSubmit = async () => {
    try {
      const payload = {
        policyHolder: {
          customerId: user.id,
          policyHolderName: formData.policyHolderName,
          policyHolderPhone: formData.policyHolderPhone,
          policyHolderAddress: formData.policyHolderAddress,
          policyNumber: formData.policyNumber,
          productName: formData.productName,
          policyPeriod: `From ${formData.policyStartDate} to ${formData.policyEndDate}`,
          tenure: formData.tenure,
          endDate: formData.policyEndDate,
          policyType: formData.policyType
        },
        insuredMembers: [],
      };

      if (!formData.policyHolderName || !formData.policyNumber) {
        showError("Please fill in all required fields.");
        return;
      }

      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/save-pdf-reader/${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        showSuccess("Policy saved successfully!");
        setManualForm(false);
        navigate("/customer/mypolicies");
      } else {
        showError(data.message || data.error || "Failed to save policy.");
      }
    } catch (err) {
      console.error("Save Policy Error:", err);
      showError("Error while saving policy.");
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStep(4);
    setUploadedFileName(file.name);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      // Get final company name (custom or selected)
      const finalCompany = selectedCompany === 'other' ? customCompany : selectedCompany;
      formData.append("insuranceCompney", finalCompany);

      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/pdf-reader/${user.id}`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to upload PDF");

      const data = await res.json();
      setPdfData(data.policyHolder || {});
      setInsuredMembers(data.insuredMembers || []);
    } catch (err) {
      console.error("PDF Upload Error:", err);
      showError("Failed to upload or process PDF");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError("Please upload a valid image file");
      return;
    }

    setStep(4);
    setUploadedFileName(file.name);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("files", file);
      // Get final company name (custom or selected)
      const finalCompany = selectedCompany === 'other' ? customCompany : selectedCompany;
      formData.append("insuranceCompney", finalCompany);

      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/image-reader/${user.id}`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to upload image");
      }

      const data = await res.json();
      
      if (data.combinedResult) {
        setPdfData(data.combinedResult.policyHolder || {});
        setInsuredMembers(data.combinedResult.insuredMembers || []);
      } else {
        setPdfData(data.policyHolder || {});
        setInsuredMembers(data.insuredMembers || []);
      }
      
      showSuccess("Image processed successfully!");
    } catch (err) {
      console.error("Image Upload Error:", err);
      showError(err.message || "Failed to upload or process image");
      setStep(3);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = () => {
    document.getElementById('cameraInput').click();
  };

  const handleSavePdfPolicy = async () => {
    try {
      const payload = {
        policyHolder: {
          customerId: user.id,
          ...pdfData,
        },
        insuredMembers,
      };

      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/save-pdf-reader/${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        showSuccess("Policy saved successfully!");
        navigate("/customer/mypolicies");
      } else {
        const errData = await res.json();
        showError(errData.error || "Failed to save policy.");
      }
    } catch (err) {
      console.error("Save Policy Error:", err);
      showError("Error while saving policy.");
    }
  };

  const InfoField = ({ label, name, value, editable, onChange }) => (
    <div className="inline-field">
      <div className="inline-label">{label}</div>
      <div className="inline-value">
        {editable && name ? (
          <Form.Control
            size="sm"
            type="text"
            name={name}
            defaultValue={value ?? ""}
            onBlur={(e) => onChange({ target: { name, value: e.target.value } })}
            className="w-100"
            autoComplete="off"
          />
        ) : (
          <div className=" text-dark" style={{ textTransform: "capitalize" }}>
            {value || "-"}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <div className="main-content">
        <div className="add-insurance-container">
          {/* Header */}
          <div className="page-header d-flex align-items-center mb-4">
            <div className="header-icon me-3">
              <i className="bi bi-plus"></i>
            </div>
            <div>
              <h2 className="page-title mb-1">Add New Policy</h2>
              <p className="page-subtitle mb-0">Add your insurance policy quickly and easily</p>
            </div>
          </div>

          {/* Toggle Buttons */}
          <div className="toggle-buttons">
            <button
              className={`toggle-btn ${!manualForm ? "active" : ""}`}
              onClick={() => setManualForm(false)}
            >
              <i className="bi bi-upload"></i>
              Upload Policy
            </button>
            <button
              className={`toggle-btn ${manualForm ? "active" : ""}`}
              onClick={() => setManualForm(true)}
            >
              <i className="bi bi-file-text"></i>
              Fill Manually
            </button>
          </div>

          {manualForm ? (
            <div className="manual-form-wrapper">
              {/* Manual Form */}
              <div className="manual-form-card">
                <div className="form-header">
                  <div className="form-icon">
                    <i className="bi bi-file-text"></i>
                  </div>
                  <h3>Add Policy Manually</h3>
                  <p>Fill in your policy details using the form below</p>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Policy Holder Name <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-person"></i>
                      <input
                        type="text"
                        name="policyHolderName"
                        value={formData.policyHolderName}
                        onChange={handleChange}
                        placeholder="Enter policy holder name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Policy Number <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-credit-card"></i>
                      <input
                        type="text"
                        name="policyNumber"
                        value={formData.policyNumber}
                        onChange={handleChange}
                        placeholder="Enter policy number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Contact <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-telephone"></i>
                      <input
                        type="text"
                        name="policyHolderPhone"
                        value={formData.policyHolderPhone}
                        onChange={handleChange}
                        placeholder="Enter contact number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Address <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-geo-alt"></i>
                      <input
                        type="text"
                        name="policyHolderAddress"
                        value={formData.policyHolderAddress}
                        onChange={handleChange}
                        placeholder="Enter address"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Insurance Company <span className="required">*</span></label>
                    <div className="select-wrapper">
                      <i className="bi bi-building"></i>
                      <select
                        name="policyType"
                        value={formData.policyType}
                        onChange={handleManualCompanyChange}
                      >
                        <option value="">Select insurance company</option>
                        {insuranceCompanies.map((company, index) => (
                          <option key={index} value={company}>
                            {company}
                          </option>
                        ))}
                        <option value="other">Other (Add manually)</option>
                      </select>
                    </div>
                  </div>

                  {formData.policyType === 'other' && (
                    <div className="form-group">
                      <label>Company Name <span className="required">*</span></label>
                      <div className="input-wrapper">
                        <i className="bi bi-building"></i>
                        <input
                          type="text"
                          placeholder="Enter insurance company name"
                          value={formData.customCompany || ''}
                          onChange={(e) => setFormData((prev) => ({ ...prev, customCompany: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Policy Name <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-file-earmark-text"></i>
                      <input
                        type="text"
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                        placeholder="Enter policy name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Policy Start Date <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-calendar"></i>
                      <input
                        type="date"
                        name="policyStartDate"
                        value={formData.policyStartDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Due Date <span className="required">*</span></label>
                    <div className="input-wrapper">
                      <i className="bi bi-calendar"></i>
                      <input
                        type="date"
                        name="policyEndDate"
                        value={formData.policyEndDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Policy Tenure <span className="required">*</span></label>
                    <div className="select-wrapper">
                      <i className="bi bi-clock"></i>
                      <select
                        name="tenure"
                        value={formData.tenure}
                        onChange={handleChange}
                      >
                        <option value="">Select tenure</option>
                        <option value="1 Year">1 Year</option>
                        <option value="2 Years">2 Years</option>
                        <option value="3 Years">3 Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Upload Insurance PDF <span className="required">*</span></label>
                    <div className="file-upload">
                      <i className="bi bi-paperclip"></i>
                      <input type="file" accept=".pdf" />
                      <span>Choose File</span>
                      <small>No file chosen</small>
                    </div>
                    <small className="help-text">Upload your insurance policy document in PDF format</small>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="submit-btn" onClick={handleManualSubmit}>
                    <i className="bi bi-check-circle"></i>
                    Add Policy
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="Insurance__stepper">
                {/* Step Progress */}
                <div className="step-progress">
                  <div className="step-info">
                    <span className="step-text">Step {step} of 3</span>
                    <span className="step-percentage">{Math.round((step / 3) * 100)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(step / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Step Indicators */}
                <div className="step-indicators">
                  {[
                    { number: 1, title: "Choose Company", subtitle: "Select your insurance provider" },
                    { number: 2, title: "Insurance Number", subtitle: "Add your policy number (optional)" },
                    { number: 3, title: "Upload Policy", subtitle: "Add your policy document" }
                  ].map((stepItem, index) => (
                    <div
                      key={index}
                      className={`step-indicator ${step > stepItem.number ? "completed" : step === stepItem.number ? "active" : ""
                        }`}
                    >
                      <div className={`step-circle ${step > stepItem.number ? "completed" : step === stepItem.number ? "active" : ""
                        }`}>
                        {step > stepItem.number ? <i className="bi bi-check"></i> : stepItem.number}
                      </div>
                      <div className="step-content">
                        <h4>{stepItem.title}</h4>
                        <p>{stepItem.subtitle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="step-content-wrapper">
                {/* Step 1: Choose Company */}
                {step === 1 && (
                  <div className="step-card-flat">
                    <div className="step-header text-center">
                      <div className="d-inline-flex align-items-center justify-content-center">
                        <div className="step-icon-flat">
                          <i className="bi bi-building"></i>
                        </div>
                        <div className="page__heading text-start">
                          <h3 className="step-title">Choose Your Insurance Company</h3>
                          <p className="step-subtitle">Select your insurance provider from the list below</p>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Dropdown */}
<div className="form-field">
  <select
    className="form-control select-flat m-0 p-0 border-0"
    value={selectedCompany}
    onChange={handleCompanyChange}
    required
    size={3}   // 👈 SHOW ONLY 4 OPTIONS
  >
    <option value="">Select your insurance company</option>

    {insuranceCompanies.map((company, index) => (
      <option key={index} value={company}>
        {company}
      </option>
    ))}

    <option value="other">Other (Add manually)</option>
  </select>
</div>

                    {/* Action */}
                    <div className="step-actions center">
                      <button
                        className="continue-btn flat"
                        onClick={() => setStep(2)}
                        disabled={!selectedCompany || (selectedCompany === 'other' && !customCompany)}
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Insurance Number */}
                {step === 2 && (
                  <div className="step-card-flat">
                    <div className="step-icon-flat">
                      <i className="bi bi-credit-card-2-front"></i>
                    </div>

                    <h3 className="step-title">Insurance Number</h3>
                    <p className="step-subtitle">Enter your policy number (optional)</p>

                    <div className="help-tip-box">
                      <i className="bi bi-info-circle"></i>
                      <span>
                        You can find your policy number on your insurance card or documents. Don't worry if you don't have it – you can skip this step!
                      </span>
                    </div>

                    <div className="form-field">
                      <input
                        type="text"
                        className="form-control input-flat"
                        value={policyNumber}
                        onChange={(e) => setPolicyNumber(e.target.value)}
                        placeholder="Enter your policy number"
                      />
                    </div>

                    <div className="step-actions between">
                      <button className="back-btn" onClick={() => setStep(1)}>← Back</button>
                      <div className="d-flex gap-2">
                        <button className="skip-btn" onClick={() => setStep(3)}>Skip</button>
                        <button className="continue-btn flat" onClick={() => setStep(3)}>
                          Continue →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Upload Policy */}
                {step === 3 && (
                  <div className="step-card-flat">
                    <div className="step-icon-flat">
                      <i className="bi bi-upload"></i>
                    </div>
                    <h3 className="step-title">Upload Your Policy</h3>
                    <p className="step-subtitle">Choose how you'd like to add your policy document</p>

                    <div className="upload-options">
                      {/* Take Picture */}
                      <div className="upload-option-box" onClick={handleCameraCapture}>
                        <div className="upload-icon camera">
                          <i className="bi bi-camera-fill"></i>
                        </div>
                        <h5>Take a Picture</h5>
                        <p>Use your camera to capture your policy document</p>
                        <input 
                          id="cameraInput"
                          type="file" 
                          accept="image/*" 
                          capture="environment"
                          onChange={handleImageUpload} 
                          style={{ display: "none" }} 
                        />
                      </div>

                      {/* Import PDF */}
                      <div className="upload-option-box">
                        <div className="upload-icon pdf">
                          <i className="bi bi-file-earmark-pdf"></i>
                        </div>
                        <h5>Import PDF</h5>
                        <p>Upload a PDF file of your policy document</p>
                        <label className="upload-label">
                          Select PDF
                          <input type="file" accept="application/pdf" onChange={handlePDFUpload} style={{ display: "none" }} />
                        </label>
                      </div>

                      {/* Upload Image */}
                      <div className="upload-option-box">
                        <div className="upload-icon pdf">
                          <i className="bi bi-image"></i>
                        </div>
                        <h5>Upload Image</h5>
                        <p>Upload an image of your policy document</p>
                        <label className="upload-label">
                          Select Image
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageUpload} 
                            style={{ display: "none" }} 
                          />
                        </label>
                      </div>
                    </div>

                    {uploading && (
                      <div className="uploading text-center mt-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Uploading...</span>
                        </div>
                        <p className="mt-2">Processing your document...</p>
                      </div>
                    )}

                    <div className="step-actions">
                      <button className="back-btn" onClick={() => setStep(2)}>← Back</button>
                    </div>
                  </div>
                )}

                {/* Step 4: PDF Summary Section */}
                {step === 4 && (
                  <div className="policy-summary-wrapper py-4 px-3 px-md-5 w-100">
                    <div className="mb-3 text-center">
                      <h4 className="mb-1">Review & Edit Policy Information</h4>
                      <p className="text-muted mb-0">Review the extracted information and make any necessary corrections</p>
                    </div>

                    {/* Policy Holder */}
                    <div className="summary-card mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="section-title mb-0">Policy Holder</h5>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditHolder(!editHolder)}>
                          <i className="bi bi-pencil"></i> {editHolder ? "Save" : "Edit"}
                        </button>
                      </div>
                      <hr />
                      <Row>
                        <Col md={6}>
                          <InfoField
                            label="Name"
                            name="policyHolderName"
                            value={pdfData.policyHolderName}
                            editable={editHolder}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Contact"
                            name="policyHolderPhone"
                            value={pdfData.policyHolderPhone}
                            editable={editHolder}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Nominee"
                            name="nomineeName"
                            value={pdfData.nomineeName || ""}
                            editable={editHolder}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField 
                            label="Nominee Relation" 
                            name="nomineeRelationship" 
                            value={pdfData.nomineeRelationship || ""} 
                            editable={editHolder}
                            onChange={handlePDFChange} 
                          />
                        </Col>
                        <Col md={12}>
                          <InfoField
                            label="Address"
                            name="policyHolderAddress"
                            value={pdfData.policyHolderAddress}
                            editable={editHolder}
                            onChange={handlePDFChange}
                            as="textarea"
                            rows={2}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Policy Details */}
                    <div className="summary-card mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="section-title mb-0">Policy Details</h5>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditPolicy(!editPolicy)}>
                          <i className="bi bi-pencil"></i> {editPolicy ? "Save" : "Edit"}
                        </button>
                      </div>
                      <hr />
                      <Row>
                        <Col md={6}>
                          <InfoField
                            label="Policy Number"
                            name="policyNumber"
                            value={pdfData.policyNumber}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Insurance Company"
                            name="productName"
                            value={pdfData.productName}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Policy Type"
                            name="policyType"
                            value={pdfData.policyType || "Term Life Insurance"}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Policy Period"
                            name="Policy Period"
                            value={pdfData?.policyPeriod}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Premium Amount"
                            name="premiumAmount"
                            value={pdfData.premiumAmount}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                            prefix="₹"
                            suffix="/year"
                          />
                        </Col>
                        <Col md={6}>
                          <InfoField
                            label="Payment Mode"
                            name="paymentMode"
                            value={pdfData.paymentMode || "Annual"}
                            editable={editPolicy}
                            onChange={handlePDFChange}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Family Members */}
                    <div className="summary-card mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="section-title mb-0">Family Members</h5>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditFamily(!editFamily)}>
                          <i className="bi bi-pencil"></i> {editFamily ? "Save" : "Edit"}
                        </button>
                      </div>
                      <hr />
                      {insuredMembers.length === 0 ? (
                        <div className="text-muted">No family members found.</div>
                      ) : (
                        <Row>
                          {insuredMembers.map((member, index) => (
                            <Col md={6} className="mb-4" key={index}>
                              <div className="family-member-box p-3 h-100">
                                <h6 className="fw-bold mb-3">Member {index + 1}</h6>

                                <InfoField
                                  label="Name"
                                  name="insuredName"
                                  value={member.insuredName}
                                  editable={editFamily}
                                  onChange={(e) =>
                                    handleInsuredMemberChange(index, "insuredName", e.target.value)
                                  }
                                />
                                <InfoField
                                  label="Relation"
                                  name="relationship"
                                  value={member.relationship}
                                  editable={editFamily}
                                  onChange={(e) =>
                                    handleInsuredMemberChange(index, "relationship", e.target.value)
                                  }
                                />
                                <InfoField
                                  label="DOB"
                                  name="dob"
                                  value={member.dob}
                                  editable={editFamily}
                                  onChange={(e) =>
                                    handleInsuredMemberChange(index, "dob", e.target.value)
                                  }
                                />
                                <InfoField
                                  label="Sum Insured"
                                  name="Sum Insured"
                                  value={member.sumInsured || ""}
                                  editable={editFamily}
                                  onChange={(e) =>
                                    handleInsuredMemberChange(index, "Sum Insured", e.target.value)
                                  }
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>

                    {/* Save Button */}
                    <div className="text-center">
                      <button className="btn btn-success px-5 py-2 fw-semibold" onClick={handleSavePdfPolicy}>
                        Save to Dashboard
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddInsurancePage;